/**
 * ClientQueue - Client queue management page
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { useAuth } from '../contexts/AuthContext';
import { useAppointment } from '../contexts/AppointmentContext';
import { useQueue } from '../hooks/useQueue';
import { useQueueSounds } from '../hooks/useQueueSounds';
import { branchService } from '../services/branchService';
import { clearClientHistory, removeExpiredTicket, getTimeRemaining } from '../services/queueService';
import { getLoyaltyConfig } from '../services/loyaltyService';
import { NotificationHistory } from '../components/notifications/NotificationHistory';
import { SoundSettings } from '../components/queue/SoundSettings';
import { SignInPrompt } from '../components/queue/client/SignInPrompt';
import { QueueHeader } from '../components/queue/client/QueueHeader';
import { ActiveTicketCard } from '../components/queue/client/ActiveTicketCard';
import { NoActiveTicketCard } from '../components/queue/client/NoActiveTicketCard';
import { CompanionTicketCard } from '../components/queue/client/CompanionTicketCard';
import { PendingAppointmentsSection } from '../components/queue/client/PendingAppointmentsSection';
import { AppointmentWarningModal } from '../components/queue/client/AppointmentWarningModal';
import { ClearHistorySection } from '../components/queue/client/ClearHistorySection';
import { QueueStatusSection } from '../components/queue/client/QueueStatusSection';
import { analyticsService } from '../services/analyticsService';
import type { Branch, Appointment, BarberStatus } from '../types';
import { toast } from '@/lib/toast';
import { breakMinutesForTicket } from '@/lib/breaks';

const DEFAULT_FRANCHISE_ID = 'lILDGxMgfsifzpqg4YZu';

export default function ClientQueue() {
  const navigate = useNavigate();
  const { user, firebaseUser, signInAsGuest, customClaims } = useAuth();
  const { subscribeToUserPendingAppointments, cancelAppointment } = useAppointment();
  const [selectedBranchId, setSelectedBranchId] = useState<string>('');
  const [branches, setBranches] = useState<Branch[]>([]);
  const [showSoundSettings, setShowSoundSettings] = useState(false);
  const [clearingHistory, setClearingHistory] = useState(false);
  const [removingTicket, setRemovingTicket] = useState(false);
  const [estimatedWaitSeconds, setEstimatedWaitSeconds] = useState<number>(0);
  const [serviceTimeRemaining, setServiceTimeRemaining] = useState<number>(0);
  const [pendingAppointments, setPendingAppointments] = useState<Appointment[]>([]);
  const [showAppointmentWarning, setShowAppointmentWarning] = useState(false);
  const [pointsSystemEnabled, setPointsSystemEnabled] = useState(false);

  // Track myTicket's barberId to filter queue by barber
  const [myTicketBarberId, setMyTicketBarberId] = useState<string>('');

  const {
    myTicket,
    myTicketLoading,
    myCompanionTickets,
    queueTickets,
    timeRemaining,
    timeRemainingFormatted,
    markArrival,
    cancelTicket,
    marking,
    cancelling,
    error
  } = useQueue({ branchId: selectedBranchId, barberId: myTicketBarberId });

  // Initialize queue sounds
  const {
    soundConfig,
    setSoundEnabled,
    playSound,
    isMonitoring
    // currentPosition
  } = useQueueSounds({
    ticketId: myTicket?.queueId,
    enableMonitoring: !!myTicket,
    events: {
      onTurnApproaching: (turnsLeft) => {
        console.log(`Your turn in ${turnsLeft} positions!`);
      },
      onTurnReady: () => {
        console.log('Your turn is ready!');
      },
      onTicketCalled: () => {
        console.log('Your ticket has been called!');
      },
    },
    alertThreshold: 3,
  });

  // When myTicket loads, set barberId to filter queue by this barber only
  useEffect(() => {
    if (myTicket?.barberId && myTicket.barberId !== 'any') {
      setMyTicketBarberId(myTicket.barberId);
    } else {
      setMyTicketBarberId('');
    }
  }, [myTicket?.barberId]);

  const userFranchiseId = user?.franchiseId || customClaims?.franchiseId;

  // Track page view
  useEffect(() => {
    analyticsService.trackPageView('client_queue');
  }, []);

  // Load loyalty config to check if points system is enabled
  useEffect(() => {
    const loadConfig = async () => {
      try {
        const config = await getLoyaltyConfig(DEFAULT_FRANCHISE_ID);
        setPointsSystemEnabled(config?.pointsSystemEnabled ?? false);
      } catch (error) {
        console.error('Error loading loyalty config:', error);
        setPointsSystemEnabled(false);
      }
    };
    loadConfig();
  }, []);

  // Subscribe to branches in real-time - filtered by user's franchise
  useEffect(() => {
    // Use franchise-specific subscription if user has a franchise
    if (userFranchiseId) {
      const unsubscribe = branchService.subscribeByFranchise(
        userFranchiseId,
        (franchiseBranches) => {
          setBranches(franchiseBranches);
        }
      );
      return () => unsubscribe();
    } else {
      // Subscribe to all branches if no franchise
      const unsubscribe = branchService.subscribe((allBranches) => {
        setBranches(allBranches);
      });
      return () => unsubscribe();
    }
  }, [userFranchiseId]);

  // Auto-select branch with active ticket, or first branch
  useEffect(() => {
    if (branches.length === 0) return;

    // Si ya hay un turno activo, auto-seleccionar su sucursal
    if (myTicket && myTicket.branchId && !selectedBranchId) {
      setSelectedBranchId(myTicket.branchId);
      return;
    }

    // Si no hay turno activo, seleccionar la primera sucursal
    if (!selectedBranchId) {
      setSelectedBranchId(branches[0].branchId);
    }
  }, [branches, myTicket, selectedBranchId]);

  // Subscribe to user's pending appointments in real-time
  useEffect(() => {
    if (!firebaseUser?.uid) {
      setPendingAppointments([]);
      return;
    }

    // Real-time subscription - updates automatically when appointments change
    const unsubscribe = subscribeToUserPendingAppointments(
      firebaseUser.uid,
      (appointments) => {
        setPendingAppointments(appointments);
      }
    );

    // Cleanup subscription on unmount
    return () => unsubscribe();
  }, [firebaseUser?.uid, subscribeToUserPendingAppointments]);

  // Estado en tiempo real de mi barbero, para sumar un descanso temporal en curso
  // (comiendo, gestión rápida…) al tiempo estimado de espera. Sin esto, la pantalla
  // decía "1h 10m" durante el almuerzo cuando la espera real eran esos 70 min más
  // lo que quedara de descanso, y el cliente se presentaba antes de tiempo.
  const [myBarberStatus, setMyBarberStatus] = useState<BarberStatus | null>(null);
  useEffect(() => {
    if (!myTicketBarberId) {
      setMyBarberStatus(null);
      return;
    }
    const unsubscribe = onSnapshot(
      doc(db, 'barberStatus', myTicketBarberId),
      (snapshot) => setMyBarberStatus(snapshot.exists() ? (snapshot.data() as BarberStatus) : null),
      (err) => {
        // Si falla, se degrada al cálculo sin descanso en vez de romper la pantalla.
        console.error('Error subscribing to barberStatus:', err);
        setMyBarberStatus(null);
      }
    );
    return () => unsubscribe();
  }, [myTicketBarberId]);

  // Real-time calculation of estimated wait time based on tickets ahead
  //
  // Para la posición 1 no se calcula: ahí la tarjeta muestra la cuenta atrás de
  // `timerExpiry`, que el backend ya alarga con el descanso del barbero.
  useEffect(() => {
    if (!myTicket || myTicket.status !== 'waiting' || myTicket.position <= 1 || !myTicketBarberId) {
      setEstimatedWaitSeconds(0);
      return;
    }

    // Calculate total wait time based on tickets ahead
    const calculateWaitTime = () => {
      // Get all tickets ahead of me for MY BARBER only (including 'finishing' status)
      const ticketsAhead = queueTickets.filter(
        (ticket) =>
          ticket.position < myTicket.position &&
          ticket.barberId === myTicket.barberId &&
          ['waiting', 'notified', 'arrived', 'in_service', 'finishing'].includes(ticket.status)
      );

      // El descanso pendiente va primero: el barbero no puede empezar ningún turno
      // hasta volver. Solo cuenta si es temporal y su hora de vuelta no ha pasado.
      let totalSeconds = 0;

      ticketsAhead.forEach((ticket) => {
        if (ticket.status === 'in_service') {
          if (ticket.timerExpiry) {
            // Ticket is in service with timer - use actual time remaining
            const timeRemainingSeconds = getTimeRemaining(ticket.timerExpiry);
            totalSeconds += timeRemainingSeconds;
          } else if (ticket.serviceStartedAt) {
            // Fallback: service in progress but no timer (old tickets before fix)
            // Calculate elapsed time and estimate remaining time
            const startedMs = ticket.serviceStartedAt.toMillis();
            const elapsedMs = Date.now() - startedMs;
            const elapsedMinutes = elapsedMs / 60000;
            const serviceDuration = ticket.serviceDuration || 30;
            const remainingMinutes = Math.max(0, serviceDuration - elapsedMinutes);
            totalSeconds += Math.ceil(remainingMinutes * 60);
          } else {
            // No timer and no start time - use full service duration
            totalSeconds += (ticket.serviceDuration || 30) * 60;
          }
        } else if (ticket.status === 'finishing' && ticket.finishingAt) {
          // Ticket is finishing soon - calculate time until finishingAt
          const now = new Date().getTime();
          const finishingAtMs = ticket.finishingAt.toMillis();
          const remainingMs = Math.max(0, finishingAtMs - now);
          totalSeconds += Math.ceil(remainingMs / 1000);
        } else {
          // Ticket is waiting/notified/arrived - use only service duration
          // Do NOT add timerExpiry (courtesy/arrival time) - that only applies to the client themselves
          totalSeconds += (ticket.serviceDuration || 30) * 60;
        }
      });

      // La pausa la cargo si mi turno se creó después de marcarla, o si ya está
      // en curso. Por fecha, no por minutos: la estimación de arriba es nominal
      // y el ancla del servidor no lo es (aviso del negocio, 7-09-2026).
      totalSeconds += breakMinutesForTicket(myBarberStatus, myTicket.createdAt, totalSeconds / 60) * 60;

      setEstimatedWaitSeconds(totalSeconds);
    };

    // Initial calculation
    calculateWaitTime();

    // Update every 1 second for real-time accuracy
    const interval = setInterval(calculateWaitTime, 1000);

    return () => clearInterval(interval);
  }, [myTicket, queueTickets, myTicketBarberId, myBarberStatus]);

  // Calculate remaining service time for in_service and finishing tickets (same logic as barber panel)
  useEffect(() => {
    if (!myTicket) {
      setServiceTimeRemaining(0);
      return;
    }

    // For finishing status, use finishingAt timestamp
    if (myTicket.status === 'finishing' && myTicket.finishingAt) {
      const calculateRemainingTime = () => {
        const finishingAtMs = myTicket.finishingAt!.toMillis();
        const now = Date.now();
        const remainingMs = Math.max(0, finishingAtMs - now);
        const remainingSeconds = Math.floor(remainingMs / 1000);
        setServiceTimeRemaining(remainingSeconds);
      };

      // Calculate initially
      calculateRemainingTime();

      // Update every second
      const interval = setInterval(calculateRemainingTime, 1000);

      return () => clearInterval(interval);
    }

    // For in_service status, calculate based on serviceStartedAt
    if (myTicket.status === 'in_service' && myTicket.serviceStartedAt) {
      const calculateRemainingTime = () => {
        const serviceStartTime = myTicket.serviceStartedAt!.toMillis();
        const now = Date.now();
        const elapsedSeconds = Math.floor((now - serviceStartTime) / 1000);
        // Use serviceDuration (actual service time) NOT estimatedWaitTime (queue wait time)
        const estimatedServiceDuration = myTicket.serviceDuration || 30;
        const totalDurationSeconds = estimatedServiceDuration * 60;
        const remaining = Math.max(0, totalDurationSeconds - elapsedSeconds);
        setServiceTimeRemaining(remaining);
      };

      // Calculate initially
      calculateRemainingTime();

      // Update every second
      const interval = setInterval(calculateRemainingTime, 1000);

      return () => clearInterval(interval);
    }

    // For other statuses, clear the timer
    setServiceTimeRemaining(0);
  }, [myTicket]);

  const handleMarkArrival = async () => {
    if (!myTicket) return;

    try {
      const waitTime = myTicket.estimatedWaitTime || 0;
      await markArrival({ queueId: myTicket.queueId });

      // Track analytics
      analyticsService.trackArrivalMarked(myTicket.queueId, waitTime);
    } catch (err) {
      console.error('Failed to mark arrival:', err);
      analyticsService.trackError(String(err), 'mark_arrival');
    }
  };

  const handleCancelTicket = async () => {
    if (!myTicket) return;

    try {
      await cancelTicket({ queueId: myTicket.queueId });

      // Play warning sound for cancellation
      playSound('warning');

      // Track analytics
      analyticsService.trackTicketCancelled(myTicket.queueId, 'Usuario canceló');
      toast.success('Turno cancelado correctamente');
    } catch (err) {
      console.error('Failed to cancel ticket:', err);
      toast.error('Error al cancelar el turno');
      analyticsService.trackError(String(err), 'cancel_ticket');
    }
  };

  const handleClearHistory = async () => {
    setClearingHistory(true);
    try {
      const result = await clearClientHistory();

      if (result.success) {
        toast.success(result.message);
        // analyticsService.trackEvent('client_history_cleared', { deletedCount: result.deletedCount });
      }
    } catch (err) {
      console.error('Failed to clear history:', err);
      toast.error('Error al limpiar el historial. Por favor intenta de nuevo.');
      analyticsService.trackError(String(err), 'clear_history');
    } finally {
      setClearingHistory(false);
    }
  };

  const handleRemoveExpiredTicket = async () => {
    if (!myTicket) return;

    try {
      setRemovingTicket(true);
      await removeExpiredTicket({ queueId: myTicket.queueId });
      playSound('success');
      toast.success('Turno eliminado correctamente');

      // Track analytics
      // Note: trackEvent not available in current analyticsService
      // analyticsService.trackEvent('ticket_removed', { queueId: myTicket.queueId, status: myTicket.status });
    } catch (err) {
      console.error('Failed to remove ticket:', err);
      toast.error('Error al eliminar turno: ' + (err instanceof Error ? err.message : String(err)));
      analyticsService.trackError(String(err), 'remove_ticket');
    } finally {
      setRemovingTicket(false);
    }
  };

  const handleTakeTicket = () => {
    // Check if user has any pending appointments
    if (pendingAppointments.length === 0) {
      navigate('/take-ticket');
      return;
    }

    // Check if any appointment is for today
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    const todayAppointments = pendingAppointments.filter(apt => {
      const aptDate = apt.dateTime.toDate();
      return aptDate >= today && aptDate < tomorrow;
    });

    if (todayAppointments.length > 0) {
      // Block if there's an appointment today
      toast.error('No puedes sacar un turno porque tienes una cita agendada para hoy. Por favor espera a que tu cita se convierta automáticamente en turno.');
      return;
    }

    // Show warning if there's an appointment on another day
    setShowAppointmentWarning(true);
  };

  const handleCancelAppointment = async (appointmentId: string) => {
    try {
      await cancelAppointment(appointmentId);
      toast.success('Cita cancelada correctamente');
      // No need to reload - real-time subscription will update automatically
    } catch (err) {
      console.error('Failed to cancel appointment:', err);
      toast.error('Error al cancelar la cita');
    }
  };

  if (!user) {
    return (
      <SignInPrompt
        onLogin={() => navigate('/login')}
        onGuestSignIn={async () => {
          try {
            await signInAsGuest();
          } catch (error) {
            console.error('Error signing in as guest:', error);
          }
        }}
      />
    );
  }

  return (
    <div className="min-h-screen bg-canvas py-8 px-4">
      <div className="max-w-4xl mx-auto">
        <QueueHeader
          showPoints={pointsSystemEnabled}
          points={user.queuePoints || 0}
          soundEnabled={soundConfig.enabled}
          onToggleSound={() => setSoundEnabled(!soundConfig.enabled)}
          onToggleSoundSettings={() => setShowSoundSettings(!showSoundSettings)}
        />

        {/* Sound settings panel */}
        {showSoundSettings && (
          <div className="mb-6">
            <SoundSettings />
          </div>
        )}

        {error && (
          <div className="mb-6 bg-danger/10 border border-danger/30 text-ink px-4 py-3 rounded-lg">
            {error}
          </div>
        )}

        {/* My ticket */}
        {myTicket ? (
          <ActiveTicketCard
            myTicket={myTicket}
            serviceTimeRemaining={serviceTimeRemaining}
            timeRemaining={timeRemaining}
            timeRemainingFormatted={timeRemainingFormatted}
            estimatedWaitSeconds={estimatedWaitSeconds}
            isMonitoring={isMonitoring}
            soundEnabled={soundConfig.enabled}
            marking={marking}
            cancelling={cancelling}
            removingTicket={removingTicket}
            onMarkArrival={handleMarkArrival}
            onCancelTicket={handleCancelTicket}
            onRemoveExpiredTicket={handleRemoveExpiredTicket}
          />
        ) : (
          <NoActiveTicketCard myTicketLoading={myTicketLoading} onTakeTicket={handleTakeTicket} />
        )}

        {/* Turnos de los acompañantes: no se renderizaban en ninguna parte del
            lado cliente, así que quien reservaba para otra persona no veía ni su
            barbero ni su posición. */}
        <CompanionTicketCard tickets={myCompanionTickets} />

        {/* Pending Appointments Section */}
        {pendingAppointments.length > 0 && (
          <PendingAppointmentsSection
            pendingAppointments={pendingAppointments}
            onCancelAppointment={handleCancelAppointment}
          />
        )}

        {/* Appointment Warning Modal */}
        {showAppointmentWarning && (
          <AppointmentWarningModal
            onCancel={() => setShowAppointmentWarning(false)}
            onContinue={() => {
              setShowAppointmentWarning(false);
              navigate('/take-ticket');
            }}
          />
        )}

        {/* Notification History */}
        <div className="bg-surface border border-hairline rounded-xl p-6 mb-6">
          <NotificationHistory />
        </div>

        {/* Clear History Section */}
        <ClearHistorySection clearingHistory={clearingHistory} onClearHistory={handleClearHistory} />

        {/* Queue status - only show when user has an active ticket AND barberId is loaded */}
        {myTicket && selectedBranchId && myTicketBarberId && (
          <QueueStatusSection
            myTicket={myTicket}
            queueTickets={queueTickets}
            currentUserId={firebaseUser?.uid}
          />
        )}
      </div>
    </div>
  );
}
